/**
 * Pure in-memory feature flag evaluator.
 *
 * This module performs NO network or file I/O. Flag evaluation is always a
 * synchronous read from the in-memory cache. The cache is populated by the
 * poller module (`feature-flag-poller.ts`), which is the only feature-flag
 * module allowed to perform I/O (fetch).
 *
 * KEY INVARIANT: flag evaluation is always synchronous from the in-memory
 * cache. `isOn` reads only from the module-level `flagCache` record.
 */

export type FlagMap = Record<string, boolean>

let flagCache: FlagMap = {}

/**
 * Replace the entire in-memory flag cache. Called by the poller after a
 * successful config refresh.
 */
export const setFlagCache = (flags: FlagMap): void => {
  flagCache = { ...flags }
}

/**
 * Reset the flag cache to empty (all flags false).
 */
export const resetFlagCache = (): void => {
  flagCache = {}
}

/**
 * Synchronously evaluate whether a feature flag is on. Returns `true` only
 * when the flag is present in the cache and explicitly enabled. Performs no
 * I/O.
 */
export const isOn = (name: string): boolean => {
  return flagCache[name] ?? false
}

/**
 * Alias for `isOn`. Synchronous flag evaluation with no I/O.
 */
export const flagEnabled = isOn

/**
 * Return a shallow copy of the current flag cache (debug/health surface).
 */
export const getAllFlags = (): FlagMap => {
  return { ...flagCache }
}
